import { useState, useCallback, useEffect, useRef } from 'react';
import * as dbOps from '../db/kioskDbOperations';

const DEVICE_TOKEN_KEY = 'kiosk_device_token';
const API_BASE = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/kiosk-auth`;
const ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;

/** Tell the kiosk service worker to pre-cache icon URLs for offline use. */
function cacheIconUrlsInSW(settings) {
  if (!navigator.serviceWorker?.controller) return;
  const urls = [settings?.icon_192_url, settings?.icon_512_url].filter(Boolean);
  if (urls.length) {
    navigator.serviceWorker.controller.postMessage({ type: 'CACHE_URLS', urls });
  }
}

// ---------------------------------------------------------------------------
// Cookie helpers — cookies are shared between Safari and iOS standalone apps,
// unlike localStorage/IndexedDB which can be isolated in legacy PWA mode.
// ---------------------------------------------------------------------------
function getKioskCookie() {
  const match = document.cookie.match(/(?:^|; )kiosk_dt=([^;]*)/);
  return match ? decodeURIComponent(match[1]) : null;
}

function setKioskCookie(token) {
  const expires = new Date(Date.now() + 365 * 864e5).toUTCString();
  document.cookie = `kiosk_dt=${encodeURIComponent(token)}; expires=${expires}; path=/kiosk; SameSite=Strict`;
}

function clearKioskCookie() {
  document.cookie = 'kiosk_dt=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/kiosk; SameSite=Strict';
}

/**
 * Kiosk device authentication hook — OFFLINE-FIRST.
 *
 * All user actions (clock in/out, breaks, PIN verify) read/write to
 * IndexedDB first and return immediately. Server communication only
 * happens during pairing, validation (background), and heartbeat.
 *
 * The sync engine (useSyncEngine) is responsible for pushing pending
 * events to the server.
 */
export function useKioskAuth() {
  const [deviceToken, setDeviceToken] = useState(
    () => localStorage.getItem(DEVICE_TOKEN_KEY) ?? null,
  );
  const [deviceInfo, setDeviceInfo] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [isValidating, setIsValidating] = useState(true);
  const [isValid, setIsValid] = useState(false);
  const [error, setError] = useState(null);

  const tokenRef = useRef(deviceToken);
  tokenRef.current = deviceToken;

  // ---------------------------------------------------------------
  // Shared API helper (used only for server calls: pair, validate, heartbeat)
  // ---------------------------------------------------------------
  const apiCall = useCallback(async (action, data = {}) => {
    try {
      const body = { action, ...data };

      if (tokenRef.current && !body.device_token) {
        body.device_token = tokenRef.current;
      }

      const response = await fetch(API_BASE, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          apikey: ANON_KEY,
        },
        body: JSON.stringify(body),
      });

      const json = await response.json();

      if (!response.ok) {
        return {
          success: false,
          error: json?.error || `Request failed with status ${response.status}`,
        };
      }

      return json;
    } catch (err) {
      return {
        success: false,
        error: err?.message || 'Network error. Please check your connection.',
      };
    }
  }, []);

  // ---------------------------------------------------------------
  // pair(pairingCode, adminCode)
  // Server call required — needs network for initial setup.
  // On success, populates IndexedDB with device config, employees,
  // break types, and starts avatar download.
  // ---------------------------------------------------------------
  const pair = useCallback(
    async (pairingCode, adminCode = null) => {
      setError(null);

      const result = await apiCall('pair', { pairing_code: pairingCode });

      if (result.success) {
        const token = result.device_token;

        // Store token in localStorage, IndexedDB, and cookie (for iOS standalone)
        localStorage.setItem(DEVICE_TOKEN_KEY, token);
        setKioskCookie(token);
        setDeviceToken(token);

        // Build device info with location details
        const info = {
          ...(result.device ?? {}),
          location_name: result.site_location?.name ?? '',
          company_name: result.company_name ?? '',
        };
        setDeviceInfo(info);

        // Populate IndexedDB
        await dbOps.setConfig('device_token', token);
        await dbOps.setConfig('device_info', info);
        await dbOps.setConfig('auto_sync', true);
        await dbOps.setConfig('last_sync_time', null);
        await dbOps.setConfig('last_employee_sync', new Date().toISOString());

        if (adminCode) {
          await dbOps.setConfig('admin_code', adminCode);
        }

        // Cache employees
        const serverEmployees = result.employees ?? [];
        await dbOps.upsertEmployees(serverEmployees);
        setEmployees(serverEmployees);

        // Cache break types if provided
        if (result.break_types) {
          await dbOps.upsertBreakTypes(result.break_types);
        }

        // Download avatars in background (non-blocking)
        const localEmployees = await dbOps.getAllEmployees();
        dbOps.downloadAndCacheAvatars(localEmployees);

        // Cache kiosk icons for offline PWA manifest
        dbOps.downloadAndCacheKioskIcons(info.settings);
        cacheIconUrlsInSW(info.settings);

        setIsValid(true);
        return { success: true, device_token: token };
      }

      setError(result.error);
      return { success: false, error: result.error };
    },
    [apiCall],
  );

  // ---------------------------------------------------------------
  // validateDevice()
  // Reads from IndexedDB first (instant, works offline).
  // If online, does background server validation + data refresh.
  // ---------------------------------------------------------------
  const validateDevice = useCallback(async () => {
    // 1. Check IndexedDB for cached device config
    const storedToken = await dbOps.getConfig('device_token');
    const storedInfo = await dbOps.getConfig('device_info');

    // Also check localStorage and cookies as fallbacks.
    // Cookies are critical for iOS: standalone web apps created via
    // apple-mobile-web-app-capable have isolated localStorage/IndexedDB
    // but SHARE cookies with Safari.
    const lsToken = localStorage.getItem(DEVICE_TOKEN_KEY);
    const cookieToken = getKioskCookie();
    const token = storedToken || lsToken || cookieToken;

    // If token was recovered from cookie, persist to localStorage + IndexedDB
    if (token && !storedToken && !lsToken && cookieToken) {
      localStorage.setItem(DEVICE_TOKEN_KEY, token);
      await dbOps.setConfig('device_token', token);
    }

    if (!token) {
      setIsValid(false);
      setIsValidating(false);
      return;
    }

    // If we have local data, trust it immediately (offline-first)
    if (storedInfo) {
      setDeviceToken(token);
      setDeviceInfo(storedInfo);
      setIsValid(true);

      // Load employees from IndexedDB
      const localEmployees = await dbOps.getAllEmployees();
      if (localEmployees.length > 0) {
        setEmployees(localEmployees);
      }

      setIsValidating(false);

      // Background: validate with server if online
      if (navigator.onLine) {
        try {
          const result = await apiCall('validate', { device_token: token });
          if (result.success) {
            // Refresh device info
            const updatedInfo = {
              ...(result.device ?? {}),
              location_name: result.site_location?.name ?? storedInfo.location_name,
              company_name: storedInfo.company_name,
            };
            setDeviceInfo(updatedInfo);
            await dbOps.setConfig('device_info', updatedInfo);

            // Cache kiosk icons for offline PWA manifest
            dbOps.downloadAndCacheKioskIcons(updatedInfo.settings);
            cacheIconUrlsInSW(updatedInfo.settings);

            // Refresh employees
            if (result.employees?.length) {
              await dbOps.upsertEmployees(result.employees);
              setEmployees(result.employees);
              await dbOps.setConfig('last_employee_sync', new Date().toISOString());
              // Download new avatars in background
              const freshEmployees = await dbOps.getAllEmployees();
              dbOps.downloadAndCacheAvatars(freshEmployees);
            }
          } else if (
            result.error?.includes('revoked') ||
            result.error?.includes('not found') ||
            result.error?.includes('inactive')
          ) {
            // Device was revoked/removed server-side — invalidate locally
            localStorage.removeItem(DEVICE_TOKEN_KEY);
            await dbOps.deleteConfig('device_token');
            setDeviceToken(null);
            setDeviceInfo(null);
            setEmployees([]);
            setIsValid(false);
          }
        } catch {
          // Server unavailable — continue with local data
        }
      }

      return;
    }

    // No local data — must validate with server (legacy device or first load)
    if (!navigator.onLine) {
      if (token) {
        // We have a token (e.g. from cookie on iOS standalone) but no cached
        // device info. Show kiosk in degraded mode instead of redirecting to
        // the pairing page — the data will sync when connectivity returns.
        setDeviceToken(token);
        setDeviceInfo({ device_name: 'Kiosk', location_name: '', company_name: '' });
        setIsValid(true);
        setIsValidating(false);
        return;
      }
      // Offline and no token — can't validate
      setIsValid(false);
      setIsValidating(false);
      setError('No cached data available. Connect to the internet to set up this device.');
      return;
    }

    setIsValidating(true);
    setError(null);

    const result = await apiCall('validate', { device_token: token });

    if (result.success) {
      const info = {
        ...(result.device ?? {}),
        location_name: result.site_location?.name ?? '',
        company_name: '',
      };

      setDeviceToken(token);
      setDeviceInfo(info);
      setIsValid(true);

      // Migrate to IndexedDB
      await dbOps.setConfig('device_token', token);
      await dbOps.setConfig('device_info', info);
      await dbOps.setConfig('auto_sync', true);

      // Cache kiosk icons for offline PWA manifest
      dbOps.downloadAndCacheKioskIcons(info.settings);
      cacheIconUrlsInSW(info.settings);

      if (result.employees?.length) {
        await dbOps.upsertEmployees(result.employees);
        setEmployees(result.employees);
        await dbOps.setConfig('last_employee_sync', new Date().toISOString());
        const localEmps = await dbOps.getAllEmployees();
        dbOps.downloadAndCacheAvatars(localEmps);
      }
    } else {
      localStorage.removeItem(DEVICE_TOKEN_KEY);
      setDeviceToken(null);
      setDeviceInfo(null);
      setEmployees([]);
      setIsValid(false);
      setError(result.error);
    }

    setIsValidating(false);
  }, [apiCall]);

  // ---------------------------------------------------------------
  // clockIn(userId, method, selfieBase64)
  // LOCAL ONLY — writes to IndexedDB and returns immediately.
  // ---------------------------------------------------------------
  const clockIn = useCallback(async (userId, method, selfieBase64 = null) => {
    try {
      console.log('[KioskAuth] clockIn called:', { userId, method, hasSelfie: !!selfieBase64, selfieLength: selfieBase64?.length || 0 });
      const { tempId, timestamp } = await dbOps.addClockEvent({
        userId,
        action: 'clock_in',
        method,
        hasSelfie: !!selfieBase64,
      });

      if (selfieBase64) {
        await dbOps.saveSelfie(tempId, selfieBase64);
        console.log('[KioskAuth] Selfie saved to IndexedDB:', { tempId, dataLength: selfieBase64.length });
      }

      return {
        success: true,
        tempId,
        check_in_time: timestamp,
      };
    } catch (err) {
      return { success: false, error: err?.message || 'Failed to record clock-in' };
    }
  }, []);

  // ---------------------------------------------------------------
  // clockOut(userId)
  // LOCAL ONLY — writes to IndexedDB, calculates duration.
  // ---------------------------------------------------------------
  const clockOut = useCallback(async (userId) => {
    try {
      const activeClockIn = await dbOps.getActiveClockIn(userId);
      const { tempId, timestamp } = await dbOps.addClockEvent({
        userId,
        action: 'clock_out',
      });

      let durationMinutes = null;
      if (activeClockIn) {
        const clockInMs = new Date(activeClockIn.timestamp).getTime();
        const clockOutMs = new Date(timestamp).getTime();
        durationMinutes = Math.round((clockOutMs - clockInMs) / 60000);
      }

      return {
        success: true,
        tempId,
        check_out_time: timestamp,
        duration_minutes: durationMinutes,
      };
    } catch (err) {
      return { success: false, error: err?.message || 'Failed to record clock-out' };
    }
  }, []);

  // ---------------------------------------------------------------
  // verifyPin(pin)
  // LOCAL ONLY — queries IndexedDB employees by PIN.
  // ---------------------------------------------------------------
  const verifyPin = useCallback(async (pin) => {
    try {
      const employee = await dbOps.getEmployeeByPin(pin);

      if (!employee) {
        return { success: false, error: 'Invalid PIN' };
      }

      // Check if user has an active clock-in
      const activeClockIn = await dbOps.getActiveClockIn(employee.id);
      const activeBreak = await dbOps.getActiveBreak(employee.id);

      return {
        success: true,
        user: {
          id: employee.id,
          display_name: employee.displayName,
          first_name: employee.firstName,
          last_name: employee.lastName,
          avatar_url: employee.avatarUrl,
          avatarBlob: employee.avatarBlob,
          clock_in_time: activeClockIn?.timestamp ?? null,
        },
        clockedIn: !!activeClockIn,
        onBreak: !!activeBreak,
        activeBreak: activeBreak
          ? { break_id: activeBreak.tempId, start_time: activeBreak.timestamp }
          : null,
      };
    } catch (err) {
      return { success: false, error: err?.message || 'PIN verification failed' };
    }
  }, []);

  // ---------------------------------------------------------------
  // getOnSiteUsers()
  // LOCAL ONLY — derived from IndexedDB clock events.
  // ---------------------------------------------------------------
  const getOnSiteUsers = useCallback(async () => {
    try {
      const onSite = await dbOps.getOnSiteUsers();
      // Transform to match expected format from server
      return onSite.map((u) => ({
        user_id: u.userId,
        display_name: u.displayName,
        first_name: u.firstName,
        last_name: u.lastName,
        avatar_url: u.avatarUrl,
        avatarBlob: u.avatarBlob,
        department: u.department,
        check_in_time: u.checkInTime,
        duration_minutes: u.durationMinutes,
        is_on_break: u.isOnBreak,
        active_break: u.activeBreak,
      }));
    } catch {
      return [];
    }
  }, []);

  // ---------------------------------------------------------------
  // startBreak(userId, breakTypeId)
  // LOCAL ONLY — writes to IndexedDB.
  // ---------------------------------------------------------------
  const startBreak = useCallback(async (userId, breakTypeId = null) => {
    try {
      const { tempId, timestamp } = await dbOps.addClockEvent({
        userId,
        action: 'break_start',
        breakTypeId,
      });

      return {
        success: true,
        break_id: tempId,
        start_time: timestamp,
      };
    } catch (err) {
      return { success: false, error: err?.message || 'Failed to start break' };
    }
  }, []);

  // ---------------------------------------------------------------
  // endBreak(userId)
  // LOCAL ONLY — writes to IndexedDB, calculates duration.
  // ---------------------------------------------------------------
  const endBreak = useCallback(async (userId) => {
    try {
      const activeBreak = await dbOps.getActiveBreak(userId);
      const { tempId, timestamp } = await dbOps.addClockEvent({
        userId,
        action: 'break_end',
      });

      let durationMinutes = null;
      if (activeBreak) {
        const startMs = new Date(activeBreak.timestamp).getTime();
        const endMs = new Date(timestamp).getTime();
        durationMinutes = Math.round((endMs - startMs) / 60000);
      }

      return {
        success: true,
        break_id: tempId,
        duration_minutes: durationMinutes,
      };
    } catch (err) {
      return { success: false, error: err?.message || 'Failed to end break' };
    }
  }, []);

  // ---------------------------------------------------------------
  // heartbeat()
  // Server call if online — refreshes employees + device info in IndexedDB.
  // No-op if offline.
  // ---------------------------------------------------------------
  const heartbeat = useCallback(async () => {
    if (!navigator.onLine) return { success: false, error: 'Offline' };

    const result = await apiCall('heartbeat');

    if (result.success) {
      if (result.employees?.length) {
        await dbOps.upsertEmployees(result.employees);
        setEmployees(result.employees);
        await dbOps.setConfig('last_employee_sync', new Date().toISOString());
        // Download new avatars in background
        const localEmps = await dbOps.getAllEmployees();
        dbOps.downloadAndCacheAvatars(localEmps);
      }
      if (result.device) {
        const updatedInfo = {
          ...result.device,
          location_name: deviceInfo?.location_name ?? '',
          company_name: deviceInfo?.company_name ?? '',
        };
        setDeviceInfo(updatedInfo);
        await dbOps.setConfig('device_info', updatedInfo);

        // Cache kiosk icons for offline PWA manifest
        dbOps.downloadAndCacheKioskIcons(updatedInfo.settings);
        cacheIconUrlsInSW(updatedInfo.settings);
      }
    } else if (
      result.error?.includes('revoked') ||
      result.error?.includes('not found') ||
      result.error?.includes('inactive')
    ) {
      // Device was revoked/removed — clear local state
      localStorage.removeItem(DEVICE_TOKEN_KEY);
      await dbOps.deleteConfig('device_token');
      setDeviceToken(null);
      setDeviceInfo(null);
      setEmployees([]);
      setIsValid(false);
    }

    return result;
  }, [apiCall, deviceInfo]);

  // ---------------------------------------------------------------
  // clearDevice()
  // Clears all local state + IndexedDB (for unpair).
  // ---------------------------------------------------------------
  const clearDevice = useCallback(async () => {
    localStorage.removeItem(DEVICE_TOKEN_KEY);
    clearKioskCookie();
    await dbOps.clearAllData();
    setDeviceToken(null);
    setDeviceInfo(null);
    setEmployees([]);
    setIsValid(false);
    setError(null);
  }, []);

  // ---------------------------------------------------------------
  // Validate on mount
  // ---------------------------------------------------------------
  useEffect(() => {
    validateDevice();
  }, [validateDevice]);

  return {
    // State
    deviceToken,
    deviceInfo,
    employees,
    isValidating,
    isValid,
    error,

    // Server actions (need network)
    pair,
    validateDevice,
    heartbeat,

    // Local actions (IndexedDB, always work)
    clockIn,
    clockOut,
    startBreak,
    endBreak,
    verifyPin,
    getOnSiteUsers,

    // Device management
    clearDevice,

    // API helper (exposed for sync engine)
    apiCall,
  };
}
